import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ExpenseList from '../components/ExpenseList';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import ClearAllConfirmationModal from '../components/ClearAllConfirmationModal';
import Navbar from '../components/Navbar';
import { useAuth } from '../utils/AuthContext';
import ExpenseService from '../services/ExpenseService';
import CategoryService from '../services/CategoryService';
import { getCategoryGradient } from '../utils/CategoryColors';

// Assign visually distinct and vibrant Tailwind gradients for up to 12 categories as fallback
const CATEGORY_COLORS = [
  'from-pink-500 to-yellow-400',
  'from-green-400 to-blue-500',
  'from-yellow-400 to-red-500',
  'from-cyan-500 to-blue-700',
  'from-purple-500 to-pink-500',
  'from-red-400 to-orange-500',
  'from-teal-400 to-green-600',
  'from-blue-400 to-indigo-600',
  'from-emerald-400 to-teal-500',
  'from-sky-500 to-indigo-400',
  'from-fuchsia-500 to-indigo-500',
  'from-lime-500 to-emerald-500',
];

const getColorByIndex = (idx) => CATEGORY_COLORS[idx % CATEGORY_COLORS.length];

const AllTransactions = () => {
  const [expenses, setExpenses] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [openClearAllDialog, setOpenClearAllDialog] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('date');
  const [filterCategory, setFilterCategory] = useState('');
  const [categories, setCategories] = useState([]);

  // Assign a vibrant color to each category
  const fetchExpensesAndCategories = async () => {
    try {
      const fetchedExpenses = await ExpenseService.getExpenses();
      setExpenses(fetchedExpenses);

      const fetchedCategories = await CategoryService.getCategories();
      setCategories(
        fetchedCategories.map((cat, idx) => ({
          value: cat,
          label: cat,
          color: getColorByIndex(idx),
          // Add textColorBlack tells downstream UI to render text as black for visibility
          textColor: 'text-black',
        }))
      );
    } catch (error) {
      console.error('Error fetching data for all transactions:', error);
    }
  };

  // Provide text color helper
  const getCategoryTextColor = (cat) => {
    const foundCategory = categories.find(c => c.value === cat);
    // Always return black for category text for best clarity
    return foundCategory && foundCategory.textColor ? foundCategory.textColor : 'text-black';
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchExpensesAndCategories();
    // eslint-disable-next-line
  }, [navigate, isAuthenticated]);

  const handleDeleteClick = (id) => {
    setExpenseToDelete(id);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await ExpenseService.deleteExpense(expenseToDelete);
      fetchExpensesAndCategories();
      setExpenseToDelete(null);
      setOpenDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const handleClearAll = async () => {
    try {
      await ExpenseService.clearAllExpenses();
      fetchExpensesAndCategories();
      setOpenClearAllDialog(false);
    } catch (error) {
      console.error('Error clearing all expenses:', error);
    }
  };

  const sortedExpenses = [...expenses].sort((a, b) => {
    if (orderBy === 'date') {
      return order === 'asc' ? new Date(a.date) - new Date(b.date) : new Date(b.date) - new Date(a.date);
    } else if (orderBy === 'amount') {
      return order === 'asc' ? a.amount - b.amount : b.amount - a.amount;
    }
    return 0;
  });

  const filteredExpenses = sortedExpenses.filter(expense =>
    expense.category.toLowerCase().includes(filterCategory.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 font-sans relative overflow-hidden">
      <Navbar />
      <br/>
      <br/>
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-4xl text-gray-800 mb-8 text-center">All Transactions</h1>

        <ExpenseList
          expenses={expenses}
          filteredExpenses={filteredExpenses}
          filterCategory={filterCategory}
          setFilterCategory={setFilterCategory}
          categories={categories}
          getCategoryGradient={getCategoryGradient}
          // Pass the black text color helper for category text
          getCategoryTextColor={getCategoryTextColor}
          handleDeleteClick={handleDeleteClick}
          setOpenClearAllDialog={setOpenClearAllDialog}
          setOrder={setOrder}
          setOrderBy={setOrderBy}
          order={order}
          orderBy={orderBy}
        />
      </div>

      <DeleteConfirmationModal
        openDeleteDialog={openDeleteDialog}
        setOpenDeleteDialog={setOpenDeleteDialog}
        handleConfirmDelete={handleConfirmDelete}
      />

      <ClearAllConfirmationModal
        openClearAllDialog={openClearAllDialog}
        setOpenClearAllDialog={setOpenClearAllDialog}
        handleClearAll={handleClearAll}
      />
    </div>
  );
};

export default AllTransactions;
